import React, { useState, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import { Wrench, ShieldAlert, Ban, Loader2, CheckCircle2, DatabaseBackup } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MaintenancePage — the destructive/bulk housekeeping tools, moved out of the
 * old monolithic dashboard: fix broken images, spam cleanup, legacy gambling
 * post unpublishing, and database backup.
 */
const MaintenancePage = () => {
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const authHeader = { Authorization: `Bearer ${token}` };

  // Fix-broken-images modal state
  const [fixOpen, setFixOpen] = useState(false);
  const [fixLoading, setFixLoading] = useState(false);
  const [fixPreview, setFixPreview] = useState(null);
  const [replacementUrl, setReplacementUrl] = useState('');
  const DEFAULT_REPLACEMENT = "https://customer-assets.emergentagent.com/job_07e05c69-0586-4274-8381-f4bbddcb034e/artifacts/bdl556y9_DJI_0001.JPG";

  // Spam cleanup modal state
  const [spamOpen, setSpamOpen] = useState(false);
  const [spamLoading, setSpamLoading] = useState(false);
  const [spamPreview, setSpamPreview] = useState(null);

  // Gambling posts unpublish modal state
  const [gpOpen, setGpOpen] = useState(false);
  const [gpLoading, setGpLoading] = useState(false);
  const [gpPreview, setGpPreview] = useState(null);
  const [gpYear, setGpYear] = useState(2023);
  const [gpMonth, setGpMonth] = useState(5);

  const [backupRunning, setBackupRunning] = useState(false);

  const openFixModal = async () => {
    setFixOpen(true);
    setFixPreview(null);
    setReplacementUrl(DEFAULT_REPLACEMENT);
    try {
      const res = await axios.get(`${API}/api/admin/broken-images-preview`, { headers: authHeader });
      setFixPreview(res.data);
    } catch (e) {
      setFixPreview({ total: 0, sample: [] });
    }
  };

  const runFix = async () => {
    setFixLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/fix-broken-images`,
        { replacement_url: replacementUrl || null },
        { headers: authHeader }
      );
      toast({ title: 'Fixed!', description: `Updated ${res.data.fixed.toLocaleString()} posts.` });
      setFixOpen(false);
    } catch (e) {
      toast({ title: 'Fix failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setFixLoading(false);
    }
  };

  const openSpamModal = async () => {
    setSpamOpen(true);
    setSpamPreview(null);
    try {
      const res = await axios.get(`${API}/api/admin/spam-cleanup-preview`, { headers: authHeader });
      setSpamPreview(res.data);
    } catch (e) {
      setSpamPreview({ total: 0, by_collection: {} });
    }
  };

  const runSpamCleanup = async () => {
    setSpamLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/spam-cleanup`, {}, { headers: authHeader });
      toast({ title: 'Spam removed', description: `Deleted ${res.data.deleted} spam entries across all collections.` });
      setSpamOpen(false);
    } catch (e) {
      toast({ title: 'Cleanup failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSpamLoading(false);
    }
  };

  const loadGpPreview = useCallback(async (year, month) => {
    setGpPreview(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/gambling-posts-preview?before_year=${year}&before_month=${month}`,
        { headers: authHeader }
      );
      setGpPreview(res.data);
    } catch (e) {
      setGpPreview({ total_to_unpublish: 0, protected_recent: 0, sample: [] });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openGpModal = () => {
    setGpOpen(true);
    loadGpPreview(gpYear, gpMonth);
  };

  const runGpCleanup = async () => {
    setGpLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/gambling-posts-unpublish`,
        { before_year: gpYear, before_month: gpMonth },
        { headers: authHeader }
      );
      toast({
        title: 'Gambling posts unpublished',
        description: `Unpublished ${res.data.unpublished} posts older than ${gpMonth}/${gpYear}. Recent posts left intact.`,
      });
      setGpOpen(false);
    } catch (e) {
      toast({ title: 'Cleanup failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setGpLoading(false);
    }
  };

  const runBackup = async () => {
    setBackupRunning(true);
    try {
      await axios.post(`${API}/api/backup-database`, {}, { headers: authHeader });
      toast({ title: 'Backup started', description: 'Database backup is running in the background.' });
    } catch (e) {
      toast({ title: 'Backup failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBackupRunning(false);
    }
  };

  const tools = [
    {
      icon: Wrench,
      iconClass: 'text-red-500 bg-red-50',
      title: 'Fix broken image paths',
      description: 'Bulk-replace broken legacy WordPress image paths across all posts with a working image URL.',
      action: openFixModal,
      cta: 'Scan & fix',
      testid: 'fix-broken-images-btn',
    },
    {
      icon: ShieldAlert,
      iconClass: 'text-red-500 bg-red-50',
      title: 'Spam cleanup',
      description: 'Removes spam directory injections (gambling, casino, betting…) from Supporters, Businesses, Advertisers and Partners.',
      action: openSpamModal,
      cta: 'Scan & clean',
      testid: 'spam-cleanup-btn',
    },
    {
      icon: Ban,
      iconClass: 'text-amber-600 bg-amber-50',
      title: 'Unpublish legacy gambling posts',
      description: 'Date-windowed unpublish (not delete) of old gambling-related posts. Recent posts stay live.',
      action: openGpModal,
      cta: 'Choose cutoff',
      testid: 'gambling-posts-btn',
    },
    {
      icon: DatabaseBackup,
      iconClass: 'text-sky-600 bg-sky-50',
      title: 'Database backup',
      description: 'Trigger a full backup of the site database to object storage. Runs in the background.',
      action: runBackup,
      cta: backupRunning ? 'Starting…' : 'Run backup',
      testid: 'db-backup-btn',
    },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="maintenance-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Maintenance</h1>
        <p className="text-sm text-slate-500 mt-1">Bulk housekeeping tools. Each one previews its impact before changing anything.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {tools.map(({ icon: Icon, iconClass, title, description, action, cta, testid }) => (
          <div key={title} className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 flex flex-col">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${iconClass}`}>
              <Icon size={18} />
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">{title}</h3>
            <p className="text-xs text-slate-500 leading-relaxed flex-1">{description}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={action}
              className="mt-4 self-start rounded-lg"
              data-testid={testid}
            >
              {cta}
            </Button>
          </div>
        ))}
      </div>

      {/* Fix Broken Images Modal */}
      <Dialog open={fixOpen} onOpenChange={setFixOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="fix-broken-images-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench size={18} className="text-red-500" /> Fix broken image paths
            </DialogTitle>
            <DialogDescription>
              Bulk-replace all broken legacy WordPress image paths with a working image URL.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {fixPreview === null ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 size={14} className="animate-spin" /> Scanning database…
              </div>
            ) : (
              <>
                <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                  <p className="text-sm text-red-900">
                    <span className="font-bold text-lg">{fixPreview.total.toLocaleString()}</span> posts will be updated.
                  </p>
                </div>

                {fixPreview.sample.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Sample of affected posts:</p>
                    <div className="bg-slate-50 rounded-lg p-3 max-h-32 overflow-y-auto text-xs space-y-1.5 font-mono">
                      {fixPreview.sample.slice(0, 5).map(p => (
                        <div key={p.id} className="text-slate-600 truncate">
                          <span className="text-slate-900 font-semibold">{p.title}</span>
                          <span className="text-red-500"> ← {p.featured_image?.slice(0, 60)}…</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Replacement image URL</label>
                  <Input
                    value={replacementUrl}
                    onChange={e => setReplacementUrl(e.target.value)}
                    placeholder={DEFAULT_REPLACEMENT}
                    className="text-xs font-mono"
                    data-testid="fix-replacement-url-input"
                  />
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                    Default is the Paisley Abbey drone shot. Paste your own URL to use a different image.
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFixOpen(false)} disabled={fixLoading} data-testid="fix-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={runFix}
              disabled={fixLoading || !fixPreview || fixPreview.total === 0}
              className="bg-red-500 hover:bg-red-600"
              data-testid="fix-confirm-btn"
            >
              {fixLoading ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> Fixing…</>
              ) : (
                <><Wrench size={14} className="mr-2" /> Fix {fixPreview?.total.toLocaleString() || 0} posts</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Spam Cleanup Modal */}
      <Dialog open={spamOpen} onOpenChange={setSpamOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="spam-cleanup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert size={18} className="text-red-500" /> Spam cleanup
            </DialogTitle>
            <DialogDescription>
              Removes spam directory injections (gambling, casino, betting, slots, poker, sportsbook, bookmaker)
              from Supporters, Businesses, Advertisers, and Featured Partners.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {spamPreview === null ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 size={14} className="animate-spin" /> Scanning…
              </div>
            ) : spamPreview.total === 0 ? (
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 text-center">
                <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-2" />
                <p className="text-sm text-emerald-900 font-medium">No spam entries found.</p>
                <p className="text-xs text-emerald-700 mt-1">Your directory is clean.</p>
              </div>
            ) : (
              <>
                <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                  <p className="text-sm text-red-900">
                    <span className="font-bold text-lg">{spamPreview.total}</span> spam entries will be permanently deleted.
                  </p>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(spamPreview.by_collection).map(([col, items]) =>
                    items.length === 0 ? null : (
                      <div key={col} className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-slate-700 mb-2 capitalize">
                          {col.replace('_', ' ')} ({items.length})
                        </p>
                        <ul className="space-y-1 text-xs text-slate-600">
                          {items.map(item => (
                            <li key={item.id} className="flex items-start gap-2">
                              <span className="text-red-500 flex-shrink-0">×</span>
                              <span className="truncate">
                                <span className="font-medium text-slate-800">{item.name || item.title}</span>
                                {item.link && <span className="text-slate-400"> → {item.link.slice(0, 40)}</span>}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSpamOpen(false)} disabled={spamLoading} data-testid="spam-cancel-btn">
              {spamPreview && spamPreview.total === 0 ? 'Close' : 'Cancel'}
            </Button>
            {spamPreview && spamPreview.total > 0 && (
              <Button
                onClick={runSpamCleanup}
                disabled={spamLoading}
                className="bg-red-500 hover:bg-red-600"
                data-testid="spam-confirm-btn"
              >
                {spamLoading ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> Deleting…</>
                ) : (
                  <><ShieldAlert size={14} className="mr-2" /> Delete {spamPreview.total} entries</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gambling posts modal */}
      <Dialog open={gpOpen} onOpenChange={setGpOpen}>
        <DialogContent className="sm:max-w-xl" data-testid="gambling-posts-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban size={18} className="text-amber-600" /> Unpublish legacy gambling posts
            </DialogTitle>
            <DialogDescription>
              Unpublishes (does NOT delete) gambling-related posts older than a chosen cutoff date.
              Match scope: <strong>title, slug, excerpt</strong> only. Posts on or after the cutoff
              are protected (kept live for revenue purposes).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Unpublish posts older than (year)</label>
                <Input
                  type="number"
                  min="2000"
                  max="2030"
                  value={gpYear}
                  onChange={e => {
                    const y = parseInt(e.target.value, 10) || 2023;
                    setGpYear(y);
                    loadGpPreview(y, gpMonth);
                  }}
                  data-testid="gp-year-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Month (1–12)</label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={gpMonth}
                  onChange={e => {
                    const m = Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 5));
                    setGpMonth(m);
                    loadGpPreview(gpYear, m);
                  }}
                  data-testid="gp-month-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Cutoff: <strong>1 {new Date(2000, gpMonth - 1).toLocaleString('en-GB', { month: 'long' })} {gpYear}</strong> —
              posts created on or after this date are <strong>protected</strong> (kept live).
            </p>

            {gpPreview === null ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 size={14} className="animate-spin" /> Scanning…
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <div className="text-2xl font-bold text-amber-700" data-testid="gp-total">
                      {gpPreview.total_to_unpublish.toLocaleString()}
                    </div>
                    <div className="text-xs text-amber-800 mt-0.5">Will be unpublished</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-700" data-testid="gp-protected">
                      {gpPreview.protected_recent.toLocaleString()}
                    </div>
                    <div className="text-xs text-emerald-800 mt-0.5 flex items-center gap-1">
                      <CheckCircle2 size={11} /> Protected (recent)
                    </div>
                  </div>
                </div>

                {gpPreview.sample.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Sample (up to 20 most recent matches):</p>
                    <div className="bg-slate-50 rounded-lg p-3 max-h-56 overflow-y-auto text-xs space-y-1.5">
                      {gpPreview.sample.map(p => (
                        <div key={p.id} className="flex gap-2 text-slate-600">
                          <span className="text-slate-400 font-mono flex-shrink-0">{p.created_at.slice(0, 10)}</span>
                          <span className="truncate text-slate-800">{p.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-sky-50 border border-sky-100 rounded-lg p-3 text-xs text-sky-900">
                  <strong>Note:</strong> This sets <code className="bg-sky-100 px-1 rounded">published: false</code>.
                  Posts remain in the database and can be re-published from Posts if needed.
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGpOpen(false)} disabled={gpLoading} data-testid="gp-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={runGpCleanup}
              disabled={gpLoading || !gpPreview || gpPreview.total_to_unpublish === 0}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="gp-confirm-btn"
            >
              {gpLoading ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> Unpublishing…</>
              ) : (
                <><Ban size={14} className="mr-2" /> Unpublish {gpPreview?.total_to_unpublish.toLocaleString() || 0} posts</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenancePage;
